import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Backdrop,
  CircularProgress,
  FormControl,
  Select,
  Pagination,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChangeHistoryIcon from "@mui/icons-material/ChangeHistory";
import SearchIcon from "@mui/icons-material/Search";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RefreshIcon from "@mui/icons-material/Refresh";

import useUserRoles from "hooks/useUserRoles";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import DashboardNavbar from "common/Navbars/DashboardNavbar";
import Footer from "common/Footer";

import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";
import DatosBasicosApi from "api/datosbasicosApi";
import SedesApi from "api/sedesApi";

import NovedadProcesoModal from "./novedadProceso";

const ProcesosTable = () => {
  const history = useHistory();
  const { user, roles } = useUserRoles();
  // Estados para paginación del servidor
  const [procesos, setProcesos] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  // Estados para filtros del servidor
  const [searchTerm, setSearchTerm] = useState("");
  const [estadoFiltro, setEstadoFiltro] = useState("");
  const [sedeFiltro, setSedeFiltro] = useState("");
  const [admitidoFiltro, setAdmitidoFiltro] = useState("");
  // Estados para opciones de filtros
  const [estadosDisponibles, setEstadosDisponibles] = useState([]);
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  // Estados generales
  const [loading, setLoading] = useState(true);
  // Estados del menú y modales
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProceso, setSelectedProceso] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState("");
  // Estados para modales
  const [modalNovedadOpen, setModalNovedadOpen] = useState(false);
  const hasFetched = useRef(false);
  const searchTimeoutRef = useRef(null);

  // FUNCIÓN PRINCIPAL PARA CARGAR DATOS - SIN DEPENDENCIAS DE ESTADO
  const fetchProcesos = useCallback(async (page, pageSize, search, estado, sede, admitido) => {
    console.log(`🔄 Cargando datos - Página: ${page}`);
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      // Solo agregar parámetros no vacíos
      if (search && search.trim()) params.search = search.trim();
      if (estado) params.estado = estado;
      if (sede) params.sede = sede;
      if (admitido) params.admitido = admitido;
      const response = await ProcesosApi.fetchProcesosPaginacion(params);
      setProcesos(response.data.results);
      setTotalItems(response.data.pagination.total_items);
      setTotalPages(response.data.pagination.total_pages);
    } catch (error) {
      console.error("Error al obtener los Procesos:", error);
      setProcesos([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar filtros disponibles
  const cargarFiltros = useCallback(async () => {
    try {
      const estadosResponse = await DatosBasicosApi.fetchEstadosProceso();
      const sedesResponse = await SedesApi.fetchSedes();
      setEstadosDisponibles(estadosResponse.data);
      setSedesDisponibles(sedesResponse.data);
    } catch (error) {
      console.error("Error al cargar filtros:", error);
    }
  }, []);

  // Effect SOLO para carga inicial
  useEffect(() => {
    if (user && !hasFetched.current) {
      console.log("🚀 Carga inicial");
      hasFetched.current = true;
      cargarFiltros();
      fetchProcesos(1, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
    }
  }, [
    user,
    roles,
    cargarFiltros,
    fetchProcesos,
    pageSize,
    searchTerm,
    estadoFiltro,
    sedeFiltro,
    admitidoFiltro,
  ]);

  // Effect SOLO para cambios de página (navegación)
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📄 Navegando a página: ${currentPage}`);
      fetchProcesos(currentPage, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
    }
  }, [currentPage]); // SOLO currentPage como dependencia

  // Effect para búsqueda con debounce
  useEffect(() => {
    if (!hasFetched.current) return;

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      console.log(`🔍 Búsqueda: "${searchTerm}"`);
      setCurrentPage(1);
      fetchProcesos(1, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]); // SOLO searchTerm

  // Effect para filtros (estado, sede, admitido)
  useEffect(() => {
    if (hasFetched.current) {
      console.log("🎛️ Aplicando filtros");
      setCurrentPage(1);
      fetchProcesos(1, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
    }
  }, [estadoFiltro, sedeFiltro, admitidoFiltro]); // SOLO estos filtros

  // Effect para cambios de pageSize
  useEffect(() => {
    if (hasFetched.current) {
      console.log(`📊 Cambiando tamaño de página: ${pageSize}`);
      setCurrentPage(1);
      fetchProcesos(1, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
    }
  }, [pageSize]); // SOLO pageSize

  // Handlers para filtros del servidor
  const handleEstadoChange = (event) => {
    setEstadoFiltro(event.target.value);
  };

  const handleSedeChange = (event) => {
    setSedeFiltro(event.target.value);
  };

  const handleAdmitidoChange = (event) => {
    setAdmitidoFiltro(event.target.value);
  };

  const handlePageChange = (event, newPage) => {
    console.log(`👆 Usuario hace clic en página: ${newPage}`);
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const limpiarFiltros = () => {
    setSearchTerm("");
    setEstadoFiltro("");
    setSedeFiltro("");
    setAdmitidoFiltro("");
    setCurrentPage(1);
  };

  // Función para recargar manteniendo página actual
  const reloadCurrentPage = () => {
    fetchProcesos(currentPage, pageSize, searchTerm, estadoFiltro, sedeFiltro, admitidoFiltro);
  };

  // Handlers del menú (mantener todos los existentes)
  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let proceso = procesos.find((a) => a.id === id);
    setSelectedProceso(proceso);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleNovedad = (proceso) => {
    setSelectedProceso(proceso);
    setModalNovedadOpen(true);
    handleMenuClose();
  };

  const handleVerResumen = (proceso) => {
    if (proceso && proceso.id) {
      history.push(`/procesos/resumen/${proceso.identificador}?origen=consultar`);
      handleMenuClose();
    }
  };

  const handleViewPdf = async (proceso) => {
    const urlResponse = await BucketApi.getProcesoFileUrlFresh(
      "pdf_solicitud.pdf",
      proceso.identificador
    );
    const newUrl = urlResponse.data.file_url;
    if (urlResponse.data.exists) {
      setCurrentPdfUrl(newUrl);
      setShowPdfViewer(true);
      handleMenuClose();
      return;
    }
    alert("El PDF de Solicitud no ha sido generado.");
    handleMenuClose();
  };

  // Columnas (mantener las existentes)
  const columns = [
    {
      field: "Opciones",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)} sx={{ padding: "2px" }}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "identificador",
      headerName: "Proceso",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.identificador}
        </SuiTypography>
      ),
    },
    {
      field: "cliente",
      headerName: "Cliente",
      flex: 3,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.cliente_nombre} ({params.row.cliente_identificacion})
        </SuiTypography>
      ),
    },
    {
      field: "sede_detalle",
      headerName: "Sede",
      sortable: false,
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.sede_ciudad || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "responsable",
      headerName: "Responsable",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.responsable_detalle
            ? params.row.responsable_detalle.usuario
              ? params.row.responsable_detalle.usuario
              : params.row.responsable_detalle.grupo
              ? "Grupo: " + params.row.responsable_detalle.grupo
              : "No tiene."
            : "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "fecha_admision",
      headerName: "Admisión",
      flex: 2,
      sortable: true,
      renderCell: (params) => {
        const fechaAdmision = params.row.fecha_admision;
        const diasRestantes = params.row.dias_habiles_restantes;
        const fechaFormateada = fechaAdmision
          ? new Date(fechaAdmision).toLocaleDateString("es-CO", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "No tiene";
        return (
          <SuiBox>
            <SuiTypography variant="caption" display="block" fontWeight="medium">
              {fechaFormateada}
            </SuiTypography>
            <SuiTypography
              variant="caption"
              display="block"
              fontWeight="bold"
              color={diasRestantes < 10 ? "error" : "textSecondary"}
              sx={{
                backgroundColor: diasRestantes < 10 ? "#ffebee" : "transparent",
                borderRadius: "4px",
                padding: "0 4px",
              }}
            >
              {diasRestantes != null ? `${diasRestantes} días restantes` : ""}
            </SuiTypography>
          </SuiBox>
        );
      },
    },
    {
      field: "estado",
      headerName: "Estado",
      sortable: true,
      flex: 2,
      renderCell: (params) => {
        const estadoDetalle = params.row.estado_nombre || "No tiene.";
        const diasRestantes = params.row.dias_habiles_restantes;
        let prioridad;
        if (diasRestantes === undefined || diasRestantes === null) {
          prioridad = "POR DEFINIR";
        } else if (diasRestantes > 40) {
          prioridad = "BAJA";
        } else if (diasRestantes >= 20) {
          prioridad = "MEDIA";
        } else {
          prioridad = "ALTA";
        }
        const style = prioridadStyles.find((item) => item.label === prioridad) || {
          backgroundColor: "gray",
          color: "white",
        };
        return (
          <Tooltip title={`Prioridad: ${prioridad}`}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: style.backgroundColor,
                color: style.color,
                padding: "4px 12px",
                borderRadius: "12px",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textAlign: "center",
                minWidth: "80px",
              }}
            >
              {estadoDetalle}
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const prioridadStyles = [
    {
      label: "POR DEFINIR",
      etiqueta: "No Admitido",
      backgroundColor: "#808b96",
      color: "white",
    },
    {
      label: "ALTA",
      etiqueta: "- de 20 días",
      backgroundColor: "#E53935",
      color: "white",
    },
    {
      label: "MEDIA",
      etiqueta: "de 20 a 40 días",
      backgroundColor: "#f39c12",
      color: "white",
    },
    {
      label: "BAJA",
      etiqueta: "+ de 40 días",
      backgroundColor: "#43A047",
      color: "white",
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox mt={1} mb={1}>
        <Grid size={12}>
          <SuiBox>
            {loading && (
              <Backdrop open={true} style={{ zIndex: 9999, color: "#fff" }}>
                <CircularProgress color="inherit" />
              </Backdrop>
            )}

            <Paper
              sx={{
                width: "100%",
                overflow: "hidden",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              {/* Header con controles */}
              <SuiBox p={1}>
                <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }} sx={{ mb: 1 }}>
                  <Grid size={4}>
                    <SuiBox display="flex" justifyContent="space-between" alignItems="center">
                      <SuiTypography variant="h4" fontWeight="bold">
                        Consulta de Procesos ({totalItems})
                      </SuiTypography>
                    </SuiBox>
                  </Grid>
                  <Grid size={8}>
                    <SuiBox display="flex" gap={1} flexWrap="wrap">
                      {prioridadStyles.map((item) => (
                        <SuiBox
                          key={item.label}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={{
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "2px 6px",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: item.backgroundColor,
                              borderRadius: "4px",
                            }}
                          />
                          <SuiTypography variant="caption">
                            {item.label} ({item.etiqueta})
                          </SuiTypography>
                        </SuiBox>
                      ))}
                    </SuiBox>
                  </Grid>
                </Grid>
                <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={12}>
                    <SuiBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 350 }}>
                        <SuiTypography variant="h6" fontWeight="light">
                          Buscar por...
                        </SuiTypography>
                        <TextField
                          size="small"
                          placeholder="identificador, cliente, auto admisión..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon />
                              </InputAdornment>
                            ),
                          }}
                          sx={{ minWidth: 400 }}
                        />
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <SuiTypography variant="h6" fontWeight="light">
                          Estado
                        </SuiTypography>
                        <Select value={estadoFiltro} label="Estado" onChange={handleEstadoChange}>
                          <MenuItem value="">Todos</MenuItem>
                          {estadosDisponibles.map((estado) => (
                            <MenuItem key={estado.id} value={estado.id}>
                              {estado.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <SuiTypography variant="h6" fontWeight="light">
                          Sede
                        </SuiTypography>
                        <Select value={sedeFiltro} label="Sede" onChange={handleSedeChange}>
                          <MenuItem value="">Todas</MenuItem>
                          {sedesDisponibles.map((sede) => (
                            <MenuItem key={sede.id} value={sede.id}>
                              {sede.ciudad}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <SuiTypography variant="h6" fontWeight="light">
                          Admitido
                        </SuiTypography>
                        <Select
                          value={admitidoFiltro}
                          label="Admitido"
                          onChange={handleAdmitidoChange}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="true">Sí</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <SuiTypography variant="h6" fontWeight="light">
                          Items x Página
                        </SuiTypography>
                        <Select
                          value={pageSize}
                          label="Tamaño"
                          onChange={(e) => handlePageSizeChange(e.target.value)}
                        >
                          <MenuItem value={10}>10</MenuItem>
                          <MenuItem value={25}>25</MenuItem>
                          <MenuItem value={50}>50</MenuItem>
                          <MenuItem value={100}>100</MenuItem>
                        </Select>
                      </FormControl>
                      <Tooltip title="Limpiar Filtros" placement="right">
                        <IconButton
                          onClick={limpiarFiltros}
                          disabled={loading}
                          size="large"
                          sx={{ ml: 1 }}
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </SuiBox>
                  </Grid>
                </Grid>
              </SuiBox>

              {/* DataGrid */}
              <DataGrid
                rows={procesos}
                columns={columns}
                loading={loading}
                getRowHeight={() => "auto"}
                sx={{
                  border: 0,
                  backgroundColor: "white",
                  borderRadius: 2,
                  [`& .${gridClasses.cell}`]: {
                    borderBottom: "none",
                    padding: "2px 2px",
                  },
                  [`& .${gridClasses.row}`]: {
                    backgroundColor: ({ palette: { white } }) => white.main,
                    "&:hover": {
                      backgroundColor: ({ palette: { grey } }) => grey[200],
                    },
                    minHeight: "4px !important",
                  },
                  [`& .${gridClasses.columnHeaders}`]: {
                    backgroundColor: ({ palette: { grey } }) => grey[50],
                    borderRadius: 1,
                    minHeight: "4px !important",
                  },
                  [`& .${gridClasses.footerContainer}`]: {
                    display: "none",
                  },
                }}
                hideFooter
              />

              {/* Paginación del servidor */}
              <SuiBox display="flex" justifyContent="center" mt={2} mb={1}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                />
              </SuiBox>
            </Paper>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem onClick={() => handleNovedad(procesos.find((a) => a.id === selectedId))}>
                <AddCircleOutlineIcon title="NOVEDADES" fontSize="small" sx={{ mr: 1 }} /> Novedades
              </MenuItem>
              <MenuItem onClick={() => handleVerResumen(procesos.find((a) => a.id === selectedId))}>
                <ChangeHistoryIcon fontSize="small" sx={{ mr: 1 }} /> Resumen
              </MenuItem>
              <MenuItem onClick={() => handleViewPdf(procesos.find((a) => a.id === selectedId))}>
                <PictureAsPdfIcon title="PDF SOLICITUD" fontSize="small" sx={{ mr: 1 }} /> Ver PDF
                Solicitud
              </MenuItem>
            </Menu>

            {/* Modales */}
            <NovedadProcesoModal
              open={modalNovedadOpen}
              onClose={() => setModalNovedadOpen(false)}
              procesoIdentificador={selectedProceso?.identificador}
              onSuccess={() => {
                reloadCurrentPage();
                setModalNovedadOpen(false);
              }}
            />
            <FileDialog
              open={showPdfViewer}
              onClose={() => setShowPdfViewer(false)}
              title="Vista previa del PDF"
              content={
                <embed
                  src={currentPdfUrl}
                  type="application/pdf"
                  width="100%"
                  height="500px"
                  style={{ border: "none" }}
                />
              }
            />
          </SuiBox>
        </Grid>
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
};

export default ProcesosTable;
